import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  WxrlAction,
  WrxlRulesService,
  WxrlActionCatalog,
  WxrlActionCatalogDetail,
  WxrlActionState,
  WxrlActionTest,
  WxrlActionText,
  WxrlActionPersonnel,
  WxrlOption,
  WxrlActionHeader,
  WxrlRelatedRule,
  assertWxrlLabNr,
  WxrlMissingLabNrError,
} from '../../services/wxrl-rules.service';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import {
  actionTargetBlockValidator,
  hasFilledGrupNrValue,
  hasFilledMethNrValue,
  inferHeredarFromLoadedMethNr,
  isActionTargetBlockActive,
  resolveActionMethNrForSave,
  shouldUseProfileOnlyFromLoaded,
} from '../../utils/action-target-selection';

@Component({
  selector: 'afoone-wxrl-action-drawer',
  templateUrl: './wxrl-action-drawer.component.html',
  styleUrls: ['./wxrl-action-drawer.component.css'],
  // ConfirmationService debe ser el del padre (p. ej. wxrl-schema con <p-confirmDialog>);
  // un provider aquí creaba otra instancia: el confirm no mostraba diálogo y performSave no corría.
})
export class WxrlActionDrawerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() visible: boolean = false;
  @Input() action: WxrlAction | null = null;
  @Input() isEditMode: boolean = false;
  @Input() isNewRule: boolean = false;
  @Input() actionStates: any[] = [];
  @Input() actionTypes: any[] = [];
  @Input() labNr?: number;
  @Input() labType: string = ''; // Tipo de laboratorio (LABM para mostrar aislamiento)
  @Input() actionHeader: WxrlActionHeader | null = null;
  /** Opciones de acciones reutilizables (RuleActionNr) para cargar desde BD */
  @Input() existingRuleActionOptions: Array<{ label: string; value: number }> = [];
  /** Número de paso a mostrar al añadir una nueva acción (número de pasos actual + 1 en lógica 0-based: length) */
  @Input() nextStepNumber: number = 0;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<any>();
  /** Solicita al padre cargar una acción reutilizable por RuleActionNr */
  @Output() requestLoadAction = new EventEmitter<number>();
  @Output() cancel = new EventEmitter<void>();

  public actionForm: FormGroup;
  public actionHeaderForm: FormGroup;

  // Opciones para dropdowns
  public actionCatalogOptions: WxrlActionCatalog[] = [];
  public actionTestOptions: WxrlActionTest[] = [];

  onMethNrShow(): void {
    // Método para manejar el evento onShow del dropdown
  }
  onMethNrHide(): void {
    // Método para manejar el evento onHide del dropdown
  }
  onMethNrClick(): void {
    // Método para manejar el evento onClick del dropdown
  }
  public actionProfileOptions: WxrlOption[] = [];
  public actionTextOptions: WxrlActionText[] = [];
  public actionPersonnelOptions: WxrlActionPersonnel[] = [];
  public actionIsolationOptions: WxrlActionState[] = [];
  public actionAdditional1Options: WxrlOption[] = [];
  public actionAdditional2Options: WxrlOption[] = [];

  // Configuración dinámica de la acción seleccionada
  public actionConfig: WxrlActionCatalogDetail | null = null;

  // Flags de visibilidad
  public showMethNr: boolean = false;
  public showGrupNr: boolean = false;
  public showTextNr: boolean = false;
  public showActionValue: boolean = false;
  public showAdditional1: boolean = false;
  public showAdditional2: boolean = false;
  public showIsolation: boolean = false; // Solo si tipo laboratorio es 'LABM'

  // Flags de tipo de control (textbox vs dropdown)
  public additional1IsDropdown: boolean = false;
  public additional2IsDropdown: boolean = false;

  // Labels dinámicos
  public labelAdditional1: string = 'Parámetro 1';
  public labelAdditional2: string = 'Parámetro 2';

  // Info de la acción seleccionada
  public actionInfo: string = '';

  // Guardar el valor anterior de methNr cuando se activa "Heredar"
  private previousMethNrValue: number | null = null;

  get showActionTargetBlock(): boolean {
    return isActionTargetBlockActive(this.showMethNr, this.showGrupNr);
  }

  get actionTargetBlockTouched(): boolean {
    return !!(
      this.actionForm.get('methNr')?.touched ||
      this.actionForm.get('grupNr')?.touched ||
      this.actionForm.get('heredar')?.touched
    );
  }

  get actionTargetBlockError(): string | null {
    if (!this.showActionTargetBlock || !this.actionTargetBlockTouched) {
      return null;
    }
    const errors = this.actionForm.errors as ValidationErrors | null;
    if (errors?.['actionTargetRequired']) {
      return 'Seleccione prueba, heredar o perfil';
    }
    if (errors?.['actionTargetExclusive']) {
      return 'Seleccione solo una opción: prueba, heredar o perfil';
    }
    return null;
  }

  // Reglas relacionadas
  public relatedRules: WxrlRelatedRule[] = [];
  public loadingRelatedRules: boolean = false;
  public userAuthorized: boolean = false;

  public selectedExistingRuleActionNr: number | null = null;

  constructor(
    private _fb: FormBuilder,
    private _translate: TranslateService,
    private _wxrlService: WrxlRulesService,
    private _toastr: ToastrService,
    private _cdr: ChangeDetectorRef,
    private _confirmationService: ConfirmationService
  ) {
    this.actionForm = this._fb.group({
      ruleState: ['', Validators.required],
      posA: [{ value: 0, disabled: true }, Validators.required],
      actionKrz: ['', Validators.required],
      labelAction: [''],
      methNr: [''],
      grupNr: [''],
      textNr: [''],
      actionValue: ['', Validators.maxLength(100)],
      additional1: ['', Validators.maxLength(2048)],
      additional2: ['', Validators.maxLength(2048)],
      kommentar: ['', Validators.maxLength(255)],
      heredar: [false],
      actionPersonnel: [''],
      isolation: [''],
    });

    this.actionHeaderForm = this._fb.group({
      ruleActionNr: [null, Validators.required], // Requerido para crear pasos
      ruleActionDesc: ['', [Validators.maxLength(100)]],
      labNr: [null],
      createdWhen: [null],
      info: ['', [Validators.maxLength(500)]],
      author: [null]
    });
  }

  ngOnInit(): void {
    this.loadActionHeader();
  }

  private updateMethNrDisabled(heredar: boolean): void {
    const methNrControl = this.actionForm.get('methNr');
    if (heredar) {
      methNrControl?.disable({ emitEvent: false });
    } else {
      methNrControl?.enable({ emitEvent: false });
    }
    // Forzar detección de cambios después de cambiar el estado disabled
    this._cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      let labOk = false;
      try {
        assertWxrlLabNr(this.labNr);
        labOk = true;
        if (this.actionCatalogOptions.length === 0) {
          void this.loadInitialData();
        }
      } catch {
        this._wxrlService.handleError(new WxrlMissingLabNrError());
      }
      if (labOk && this.action) {
        // Cargar datos iniciales y luego establecer valores
        this.loadAndSetActionData();
      } else if (labOk && !this.isEditMode && !this.action) {
        // Nueva acción: popular posA con el siguiente número de paso (0-based = nº pasos actuales).
        // No aplicar si ya hay payload de acción (edición): closeAllDrawers puede dejar isEditMode
        // en false un ciclo antes de que llegue editingAction (#557).
        requestAnimationFrame(() => {
          if (this.action || this.isEditMode) {
            return;
          }
          const posAControl = this.actionForm.get('posA');
          if (posAControl && this.nextStepNumber !== undefined && this.nextStepNumber !== null) {
            posAControl.setValue(this.nextStepNumber, { emitEvent: false });
            this._cdr.detectChanges();
          }
        });
      }
      this.loadActionHeader();
    } else if (changes['visible'] && !this.visible) {
      this.actionForm.reset();
      this.resetVisibilityFlags();
      // Limpiar el valor guardado cuando se cierra el drawer
      this.previousMethNrValue = null;
    }

    // #557: si el drawer ya está visible y llega el payload de acción de edición más tarde,
    // rehidratar formulario para mantener el posA existente en vez del "siguiente paso".
    if (changes['action'] && this.visible && this.isEditMode && this.action) {
      this.loadAndSetActionData();
    }

    if (changes['actionHeader']) {
      this.loadActionHeader();
    }

    if (changes['labNr']) {
      try {
        assertWxrlLabNr(changes['labNr'].currentValue);
        void this.loadInitialData();
      } catch {
        this.actionCatalogOptions = [];
      }
    }
  }

  private async loadAndSetActionData(): Promise<void> {
    // Asegurar que las opciones estén cargadas antes de hacer patchValue
    if (this.actionPersonnelOptions.length === 0) {
      await this.loadInitialData();
    }

    let persNrValue: number | null = null;
    if (this.action.persNr !== undefined && this.action.persNr !== null) {
      if (typeof this.action.persNr === 'string') {
        persNrValue = Number.parseInt(this.action.persNr, 10);
      } else {
        persNrValue = this.action.persNr;
      }
    }
    // Mapear persNr del backend a actionPersonnel del formulario
    const actionData: any = { ...this.action };
    if (persNrValue !== null) {
      actionData.actionPersonnel = persNrValue;
    }
    delete actionData.persNr;

    // Asegurar que ruleState sea el código (Schl) para el backend, no la etiqueta (Schluessel)
    if (actionData.ruleState && this.actionStates?.length > 0) {
      const match = this.actionStates.find(
        (s: any) =>
          (s.value && String(s.value) === String(actionData.ruleState)) ||
          (s.label && String(s.label).toLowerCase() === String(actionData.ruleState).toLowerCase())
      );
      if (match?.value != null) {
        actionData.ruleState = match.value;
      }
    }

    // Guardar valores antes de cargar configuración
    const methNrValue = actionData.methNr;

    // Si methNr es -1, significa que heredar está marcado
    if (inferHeredarFromLoadedMethNr(methNrValue)) {
      actionData.heredar = true;
      actionData.methNr = null;
    } else if (shouldUseProfileOnlyFromLoaded(methNrValue, actionData.grupNr)) {
      actionData.heredar = false;
      actionData.methNr = null;
    } else {
      actionData.heredar = false;
    }

    // Si hay actionKrz, cargar configuración primero
    if (this.action.actionKrz) {
      await this.onActionChange(this.action.actionKrz);
    }

    // Hacer patchValue después de que onActionChange termine
    // Usar requestAnimationFrame para evitar ExpressionChangedAfterItHasBeenCheckedError
    requestAnimationFrame(() => {
      // Si methNr está deshabilitado, habilitarlo temporalmente para establecer el valor
      const methNrControl = this.actionForm.get('methNr');
      const wasMethNrDisabled = methNrControl?.disabled;
      if (wasMethNrDisabled && methNrValue !== undefined && methNrValue !== null && methNrValue !== '') {
        methNrControl?.enable({ emitEvent: false });
      }

      this.actionForm.patchValue(actionData, { emitEvent: false });
      // posA está disabled: patchValue no lo actualiza; setValue sí (#557).
      const posAValue = actionData.posA;
      if (posAValue !== undefined && posAValue !== null) {
        const posAControl = this.actionForm.get('posA');
        const posANum = typeof posAValue === 'string' ? Number.parseInt(posAValue, 10) : posAValue;
        if (posAControl && Number.isFinite(posANum)) {
          posAControl.setValue(posANum, { emitEvent: false });
        }
      }

      // Restaurar el estado disabled de methNr si estaba deshabilitado
      if (wasMethNrDisabled) {
        const heredarValue = this.actionForm.get('heredar')?.value;
        if (heredarValue !== undefined) {
          this.updateMethNrDisabled(heredarValue);
        }
      } else {
        // Actualizar estado disabled de methNr basado en heredar
      const heredarValue = this.actionForm.get('heredar')?.value;
      if (heredarValue !== undefined) {
        this.updateMethNrDisabled(heredarValue);
      }
      }

      // Forzar la actualización del dropdown si persNr tiene valor
      if (persNrValue !== null) {
        // Verificar que el valor existe en las opciones disponibles
        const valueExists = this.actionPersonnelOptions.some(
          option => option.value === persNrValue
        );
        if (valueExists) {
          const personnelControl = this.actionForm.get('actionPersonnel');
          if (personnelControl) {
            personnelControl.setValue(persNrValue, { emitEvent: false });
          }
        }
      }

      // Forzar la actualización del dropdown de methNr si tiene valor
      // Esperar un poco más para asegurar que las opciones estén cargadas
      setTimeout(() => {
        if (methNrValue !== undefined && methNrValue !== null && methNrValue !== '') {
          const methNrValueNum = typeof methNrValue === 'string' ? Number.parseInt(methNrValue, 10) : methNrValue;
          // Verificar que las opciones estén cargadas y que el valor existe
          if (this.actionTestOptions.length > 0) {
            const methNrExists = this.actionTestOptions.some(
              (option: any) => option.value === methNrValueNum
            );
            if (methNrExists) {
              const methNrControl = this.actionForm.get('methNr');
              if (methNrControl && !methNrControl.disabled) {
                methNrControl.setValue(methNrValueNum, { emitEvent: false });
                this._cdr.detectChanges();
              }
            }
          }
        }
      }, 100);

      // Forzar detección de cambios para el textarea de PrimeNG
      this._cdr.detectChanges();
      this._cdr.markForCheck();
    });
  }

  async loadInitialData(): Promise<void> {
    let lab: number;
    try {
      lab = assertWxrlLabNr(this.labNr);
    } catch {
      this._wxrlService.handleError(new WxrlMissingLabNrError());
      return;
    }
    try {
      // Cargar catálogo de acciones
      this.actionCatalogOptions = await this._wxrlService.getActionCatalog(lab);

      // Cargar estados (si no vienen como input)
      if (!this.actionStates || this.actionStates.length === 0) {
        const states = await this._wxrlService.getActionStates();
        this.actionStates = states.map((state) => ({
          label: state.schluessel,
          value: state.schl,
        }));
      }

      // Cargar personal
      this.actionPersonnelOptions = await this._wxrlService.getActionPersonnel();

      // Cargar aislamiento (solo si tipo de laboratorio es 'LABM')
      if (this.labType === 'LABM') {
      const isolation = await this._wxrlService.getActionIsolation();
      this.actionIsolationOptions = isolation;
        this.showIsolation = true;
      } else {
        this.showIsolation = false;
        this.actionIsolationOptions = [];
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      this._toastr.error('Error al cargar los datos iniciales', 'Error');
    }
  }

  async onActionChange(actionKrz: string): Promise<void> {
    if (!actionKrz) {
      this.actionForm.patchValue({ labelAction: '' }, { emitEvent: false });
      this.resetVisibilityFlags();
      return;
    }

    try {
      // Cargar configuración de la acción
      this.actionConfig = await this._wxrlService.getActionCatalogDetail(actionKrz);

      // Actualizar info
      const selectedAction = this.actionCatalogOptions.find((a) => a.actionKrz === actionKrz);
      const labelFromCatalog = (selectedAction?.labelAction ?? '').trim();
      this.actionForm.patchValue(
        { labelAction: labelFromCatalog },
        { emitEvent: false }
      );
      this.actionInfo = selectedAction?.info || '';

      // Actualizar flags de visibilidad
      this.showMethNr = this.actionConfig.enabledMethNr === 1;
      this.showGrupNr = this.actionConfig.enabledGrupNr === 1;
      this.showTextNr = this.actionConfig.enabledTextNr === 1;
      this.showActionValue = this.actionConfig.enabledActionValue === 1;
      this.showAdditional1 = this.actionConfig.enabledAdditional1 === 1;
      this.showAdditional2 = this.actionConfig.enabledAdditional2 === 1;

      // Actualizar labels dinámicos
      this.labelAdditional1 = this.actionConfig.labelAdditional1 || 'Parámetro 1';
      this.labelAdditional2 = this.actionConfig.labelAdditional2 || 'Parámetro 2';

      // Determinar tipo de control para parámetros adicionales
      this.additional1IsDropdown = this.actionConfig.add1Table === 1;
      this.additional2IsDropdown = this.actionConfig.add2Table === 1;

      // Limpiar campos que ahora están ocultos
      this.clearHiddenFields();

      // Cargar opciones según campos visibles
      await this.loadActionOptions(actionKrz);

      // Actualizar validadores
      this.updateFormValidators();
    } catch (error) {
      console.error('Error loading action detail:', error);
      this._toastr.error('Error al cargar la configuración de la acción', 'Error');
    }
  }

  async loadActionOptions(actionKrz?: string): Promise<void> {
    // Si no se pasa como parámetro, intentar obtenerlo del formulario
    const actionKrzValue = actionKrz || this.actionForm.get('actionKrz')?.value;
    if (!actionKrzValue) {
      return;
    }

    try {
      let lab: number;
      try {
        lab = assertWxrlLabNr(this.labNr);
      } catch {
        this._wxrlService.handleError(new WxrlMissingLabNrError());
        return;
      }
      // Cargar pruebas si está visible
      if (this.showMethNr) {
        this.actionTestOptions = await this._wxrlService.getActionTests(lab);
      }

      // Cargar perfiles si está visible
      if (this.showGrupNr) {
        const profiles = await this._wxrlService.getPerfiles(lab);
        this.actionProfileOptions = profiles.map((p: any) => ({
          value: p.grupNr,
          label: p.grupName || `${p.grupNr}`,
        }));
      }

      // Cargar textos si está visible
      if (this.showTextNr) {
        this.actionTextOptions = await this._wxrlService.getActionTexts(lab);
      }

      // Cargar parámetro adicional 1 si es dropdown
      if (this.showAdditional1 && this.additional1IsDropdown) {
        this.actionAdditional1Options = await this._wxrlService.getActionAdditional1Options(
          actionKrzValue,
          lab
        );
      }

      // Cargar parámetro adicional 2 si es dropdown
      if (this.showAdditional2 && this.additional2IsDropdown) {
        this.actionAdditional2Options = await this._wxrlService.getActionAdditional2Options(
          actionKrzValue,
          lab
        );
      }
    } catch (error) {
      console.error('Error loading action options:', error);
    }
  }

  updateFormValidators(): void {
    const methNrControl = this.actionForm.get('methNr');
    const grupNrControl = this.actionForm.get('grupNr');
    const textNrControl = this.actionForm.get('textNr');
    const additional1Control = this.actionForm.get('additional1');
    const additional2Control = this.actionForm.get('additional2');

    if (this.showActionTargetBlock) {
      methNrControl?.clearValidators();
      grupNrControl?.clearValidators();
      this.actionForm.setValidators([actionTargetBlockValidator()]);
    } else {
      this.actionForm.clearValidators();

      if (this.showMethNr) {
        const heredar = this.actionForm.get('heredar')?.value;
        this.updateMethNrValidation(heredar);
      } else {
        methNrControl?.clearValidators();
        methNrControl?.updateValueAndValidity({ emitEvent: false });
      }

      if (this.showGrupNr) {
        grupNrControl?.setValidators([Validators.required]);
      } else {
        grupNrControl?.clearValidators();
      }
    }

    methNrControl?.updateValueAndValidity({ emitEvent: false });
    grupNrControl?.updateValueAndValidity({ emitEvent: false });
    this.actionForm.updateValueAndValidity({ emitEvent: false });

    // Texto: required si está visible
    if (this.showTextNr) {
      textNrControl?.setValidators([Validators.required]);
    } else {
      textNrControl?.clearValidators();
    }
    textNrControl?.updateValueAndValidity({ emitEvent: false });

    // Parámetro 1: required si está visible, maxLength siempre
    if (this.showAdditional1) {
      additional1Control?.setValidators([Validators.required, Validators.maxLength(2048)]);
    } else {
      additional1Control?.setValidators([Validators.maxLength(2048)]);
    }
    additional1Control?.updateValueAndValidity({ emitEvent: false });

    // Parámetro 2: required si está visible, maxLength siempre
    if (this.showAdditional2) {
      additional2Control?.setValidators([Validators.required, Validators.maxLength(2048)]);
    } else {
      additional2Control?.setValidators([Validators.maxLength(2048)]);
    }
    additional2Control?.updateValueAndValidity({ emitEvent: false });

    // Aislamiento: required si está visible
    const isolationControl = this.actionForm.get('isolation');
    if (this.showIsolation) {
      isolationControl?.setValidators([Validators.required]);
    } else {
      isolationControl?.clearValidators();
    }
    isolationControl?.updateValueAndValidity({ emitEvent: false });
  }

  updateMethNrValidation(heredar: boolean): void {
    const methNrControl = this.actionForm.get('methNr');
    if (this.showActionTargetBlock) {
      if (heredar) {
        const currentValue = methNrControl?.value;
        if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
          this.previousMethNrValue = currentValue;
        }
        methNrControl?.clearValidators();
        methNrControl?.setValue(null, { emitEvent: false });
      } else {
        methNrControl?.clearValidators();
        if (this.previousMethNrValue !== null && this.previousMethNrValue !== undefined) {
          const valueExists = this.actionTestOptions.some(
            (option: any) => option.value === this.previousMethNrValue
          );
          if (valueExists) {
            methNrControl?.setValue(this.previousMethNrValue, { emitEvent: false });
          } else {
            this.previousMethNrValue = null;
          }
        }
      }
      methNrControl?.updateValueAndValidity({ emitEvent: false });
      this.actionForm.updateValueAndValidity({ emitEvent: false });
      return;
    }

    if (heredar) {
      // Guardar el valor actual antes de limpiarlo
      const currentValue = methNrControl?.value;
      if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
        this.previousMethNrValue = currentValue;
      }
      methNrControl?.clearValidators();
      methNrControl?.setValue(null, { emitEvent: false });
    } else {
      // Restaurar el valor anterior si existe
      methNrControl?.setValidators([Validators.required]);
      if (this.previousMethNrValue !== null && this.previousMethNrValue !== undefined) {
        // Verificar que el valor existe en las opciones disponibles
        const valueExists = this.actionTestOptions.some(
          (option: any) => option.value === this.previousMethNrValue
        );
        if (valueExists) {
          methNrControl?.setValue(this.previousMethNrValue, { emitEvent: false });
        } else {
          // Si el valor no existe en las opciones, limpiar
          this.previousMethNrValue = null;
        }
      }
    }
    methNrControl?.updateValueAndValidity({ emitEvent: false });
  }

  onHeredarChange(checked: boolean): void {
    if (checked && this.showActionTargetBlock) {
      this.actionForm.patchValue({ grupNr: null }, { emitEvent: false });
    }
    // Usar requestAnimationFrame para evitar ExpressionChangedAfterItHasBeenCheckedError
    // Esto asegura que los cambios se ejecuten después del ciclo de detección de cambios
    requestAnimationFrame(() => {
      // Asegurar que las opciones estén cargadas antes de restaurar el valor
      if (!checked && this.actionTestOptions.length === 0 && this.showMethNr) {
        // Si no hay opciones cargadas y se está desactivando heredar, cargarlas
        const actionKrz = this.actionForm.get('actionKrz')?.value;
        if (actionKrz) {
          this.loadActionOptions(actionKrz).then(() => {
            this.updateMethNrValidation(checked);
            this.updateMethNrDisabled(checked);
            this.updateFormValidators();
            this._cdr.detectChanges();
          });
        } else {
          this.updateMethNrValidation(checked);
          this.updateMethNrDisabled(checked);
          this.updateFormValidators();
          this._cdr.detectChanges();
        }
      } else {
        this.updateMethNrValidation(checked);
        this.updateMethNrDisabled(checked);
        this.updateFormValidators();
        this._cdr.detectChanges();
      }
    });
  }

  onMethNrChange(): void {
    if (!this.showActionTargetBlock) {
      this.updateFormValidators();
      return;
    }
    queueMicrotask(() => {
      const methNr = this.actionForm.get('methNr')?.value;
      if (!hasFilledMethNrValue(methNr)) {
        this.updateFormValidators();
        this._cdr.markForCheck();
        return;
      }
      this.actionForm.patchValue({ grupNr: null, heredar: false }, { emitEvent: false });
      this.updateMethNrDisabled(false);
      this.updateFormValidators();
      this._cdr.markForCheck();
    });
  }

  onGrupNrChange(): void {
    if (!this.showActionTargetBlock) {
      this.updateFormValidators();
      return;
    }
    queueMicrotask(() => {
      const grupNr = this.actionForm.get('grupNr')?.value;
      if (!hasFilledGrupNrValue(grupNr)) {
        this.updateFormValidators();
        this._cdr.markForCheck();
        return;
      }
      this.actionForm.patchValue({ methNr: null, heredar: false }, { emitEvent: false });
      this.previousMethNrValue = null;
      this.updateMethNrDisabled(false);
      this.updateFormValidators();
      this._cdr.markForCheck();
    });
  }

  clearHiddenFields(): void {
    if (!this.showMethNr) {
      this.actionForm.get('methNr')?.setValue(null);
      this.actionForm.get('heredar')?.setValue(false);
    }
    if (!this.showGrupNr) {
      this.actionForm.get('grupNr')?.setValue(null);
    }
    if (!this.showTextNr) {
      this.actionForm.get('textNr')?.setValue(null);
    }
    if (!this.showActionValue) {
      this.actionForm.get('actionValue')?.setValue('');
    }
    if (!this.showAdditional1) {
      this.actionForm.get('additional1')?.setValue('');
      this.actionAdditional1Options = [];
    }
    if (!this.showAdditional2) {
      this.actionForm.get('additional2')?.setValue('');
      this.actionAdditional2Options = [];
    }
  }

  resetVisibilityFlags(): void {
    this.showMethNr = false;
    this.showGrupNr = false;
    this.showTextNr = false;
    this.showActionValue = false;
    this.showAdditional1 = false;
    this.showAdditional2 = false;
    this.actionConfig = null;
    this.actionInfo = '';
    this.labelAdditional1 = 'Parámetro 1';
    this.labelAdditional2 = 'Parámetro 2';
    this.additional1IsDropdown = false;
    this.additional2IsDropdown = false;
  }

  public showActionInfo: boolean = false;
  private infoTimeout: any = null;

  onInfoClick(): void {
    if (this.actionInfo) {
      // Cerrar si ya está abierto
      if (this.showActionInfo) {
        this.closeActionInfo();
        return;
      }

      // Mostrar el mensaje
      this.showActionInfo = true;
      this._cdr.detectChanges();

      // Cerrar automáticamente después de 3 segundos
      if (this.infoTimeout) {
        clearTimeout(this.infoTimeout);
      }
      this.infoTimeout = setTimeout(() => {
        this.closeActionInfo();
      }, 3000);
    }
  }

  closeActionInfo(): void {
    this.showActionInfo = false;
    if (this.infoTimeout) {
      clearTimeout(this.infoTimeout);
      this.infoTimeout = null;
    }
    this._cdr.detectChanges();
  }

  ngOnDestroy(): void {
    // Limpiar el timeout si el componente se destruye
    if (this.infoTimeout) {
      clearTimeout(this.infoTimeout);
      this.infoTimeout = null;
    }
  }


  private loadActionHeader(): void {
    if (this.actionHeader) {
      requestAnimationFrame(() => {
        this.actionHeaderForm.patchValue({
          ruleActionNr: this.actionHeader.ruleActionNr,
          ruleActionDesc: this.actionHeader.ruleActionDesc,
          labNr: this.actionHeader.labNr,
          createdWhen: this.actionHeader.createdWhen,
          info: this.actionHeader.info,
          author: this.actionHeader.author
        });
        this._cdr.detectChanges();
      });

      // Cargar reglas relacionadas si hay ruleActionNr
      if (this.actionHeader.ruleActionNr) {
        this.loadRelatedRules(this.actionHeader.ruleActionNr);
      }
    } else {
      // Si no hay actionHeader pero estamos creando una nueva acción, inicializar el formulario vacío
      if (!this.isEditMode && this.visible) {
        requestAnimationFrame(() => {
          this.actionHeaderForm.patchValue({
            ruleActionNr: null,
            ruleActionDesc: '',
            labNr: this.labNr || null,
            createdWhen: null,
            info: '',
            author: null
          });
          this._cdr.detectChanges();
        });
      }
      this.relatedRules = [];
      this.userAuthorized = false;
    }
  }

  private async loadRelatedRules(ruleActionNr: number): Promise<void> {
    try {
      this.loadingRelatedRules = true;
      this.relatedRules = await this._wxrlService.getRelatedRules(ruleActionNr);
      // Si hay más de una regla, resetear la autorización
      if (this.relatedRules.length > 1) {
        this.userAuthorized = false;
      } else {
        this.userAuthorized = true;
      }
    } catch (error) {
      console.error('Error loading related rules:', error);
      this.relatedRules = [];
    } finally {
      this.loadingRelatedRules = false;
      this._cdr.detectChanges();
    }
  }

  onRequestLoadExistingAction(): void {
    if (!this.selectedExistingRuleActionNr) return;
    this.requestLoadAction.emit(this.selectedExistingRuleActionNr);
  }

  onSave(): void {
    // Verificar si se requiere autorización (más de una regla relacionada).
    // Al añadir un nuevo paso (!isEditMode) no bloquear: solo aplicar al editar una acción existente.
    if (this.isEditMode && this.relatedRules.length > 1 && !this.userAuthorized) {
      this._translate.get('WXRL.ACTION.AUTHORIZATION_REQUIRED', { 0: this.relatedRules.length }).subscribe((message) => {
        this._confirmationService.confirm({
          message: message,
          header: this._translate.instant('WXRL.ACTION.RELATED_RULES'),
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: this._translate.instant('OK'),
          rejectLabel: this._translate.instant('CANCEL'),
          accept: () => {
            this.userAuthorized = true;
            this.performSave();
          },
          reject: () => {
            // No hacer nada, el usuario canceló
          }
        });
      });
      return;
    }

    this.performSave();
  }

  private hasHeaderContext(): boolean {
    return !!(this.actionHeader || !this.isEditMode);
  }

  private hasRuleActionNrValue(): boolean {
    const ruleActionNrValue = this.actionHeaderForm.get('ruleActionNr')?.value;
    return ruleActionNrValue != null && ruleActionNrValue !== '';
  }

  private hasActionData(): boolean {
    // #555: en edición de paso existente siempre hay contexto de paso; si no, un ruleState
    // falsy (p. ej. 0) o campos vacíos momentáneos hacían saltar el guardado solo cabecera (#554)
    // y se perdían cambios en methNr, textNr, etc.
    if (this.isEditMode) {
      return true;
    }
    return !!(
      this.actionForm.get('ruleState')?.value ||
      this.actionForm.get('actionKrz')?.value
    );
  }

  private canSaveHeaderOnly(): boolean {
    return this.hasHeaderContext() && this.hasRuleActionNrValue() && this.actionHeaderForm.valid;
  }

  private performSave(): void {
    const isActionFormValid = this.actionForm.valid;
    const hasHeaderContext = this.hasHeaderContext();
    const hasActionData = this.hasActionData();
    const canSaveHeaderOnly = this.canSaveHeaderOnly();
    const isActionHeaderFormValid = hasHeaderContext ? this.actionHeaderForm.valid : true;

    // #554: Permitir guardar solo cabecera sin forzar validación del paso (solo alta de paso).
    // #555: Nunca en edición de paso existente: evitar emitir solo cabecera y descartar el paso.
    if (!this.isEditMode && !hasActionData && canSaveHeaderOnly) {
      this.save.emit({ actionHeader: this.actionHeaderForm.value });
      return;
    }

    if (isActionFormValid && isActionHeaderFormValid) {
      const formValue = this.actionForm.value;
      // Usar getRawValue() para incluir campos deshabilitados como posA
      const rawFormValue = this.actionForm.getRawValue();
      // Mapear actionPersonnel del formulario a persNr para el backend
      const actionData = { ...formValue, ...rawFormValue };
      // Asegurar que posA esté incluido (puede estar deshabilitado)
      if (rawFormValue.posA !== undefined && rawFormValue.posA !== null) {
        actionData.posA = rawFormValue.posA;
      }
      if (actionData.actionPersonnel !== undefined && actionData.actionPersonnel !== null && actionData.actionPersonnel !== '') {
        actionData.persNr = actionData.actionPersonnel;
      }
      delete actionData.actionPersonnel;

      // Manejar objetivo (prueba / heredar / perfil)
      const resolvedMethNr = resolveActionMethNrForSave({
        heredar: actionData.heredar,
        methNr: rawFormValue.methNr ?? actionData.methNr,
        grupNr: actionData.grupNr,
      });
      if (resolvedMethNr !== null) {
        actionData.methNr = resolvedMethNr;
      }
      delete actionData.heredar;

      // Incluir actionHeader si existe y tiene valores
      const actionHeaderValue = this.actionHeaderForm.value;
      const hasActionHeader = this.actionHeader && (
        actionHeaderValue.ruleActionNr ||
        actionHeaderValue.ruleActionDesc ||
        actionHeaderValue.labNr ||
        actionHeaderValue.info
      );

      const emitData = hasActionHeader ? {
        action: actionData,
        actionHeader: actionHeaderValue
      } : actionData;
      this.save.emit(emitData);
    } else {
      // Marcar como touched los campos inválidos
      if (hasActionData && !isActionFormValid) {
        Object.keys(this.actionForm.controls).forEach((key) => {
          this.actionForm.get(key)?.markAsTouched();
        });
      }
      if (hasHeaderContext && !isActionHeaderFormValid) {
        Object.keys(this.actionHeaderForm.controls).forEach((key) => {
          this.actionHeaderForm.get(key)?.markAsTouched();
        });
      }
    }
  }

  onCancel(): void {
    this.visibleChange.emit(false);
    this.cancel.emit();
  }

  onVisibleChange(value: boolean): void {
    this.visibleChange.emit(value);
  }

  /** Texto completo en catálogo (tooltip en trigger truncado; #553). */
  get selectedActionCatalogLabel(): string {
    const krz = this.actionForm?.get('actionKrz')?.value;
    if (!krz) {
      return '';
    }
    const opt = this.actionCatalogOptions.find((a) => a.actionKrz === krz);
    return (opt?.labelAction ?? '').trim();
  }

  get drawerTitle(): string {
    return this.isEditMode
      ? this._translate.instant('WXRL.SCHEMA.EDIT_ACTION')
      : this._translate.instant('WXRL.SCHEMA.NEW_ACTION');
  }

  get canSave(): boolean {
    // Si hay actionHeader o estamos creando nueva acción, la cabecera debe estar completa y válida
    if (this.hasHeaderContext()) {
      // Si no hay ruleActionNr, no se puede guardar (la cabecera no está completa)
      if (!this.hasRuleActionNrValue()) {
        return false;
      }

      // Si la cabecera tiene cambios y no es válida, no se puede guardar
      if (this.actionHeaderForm.dirty && !this.actionHeaderForm.valid) {
        return false;
      }

      // Si hay ruleActionNr y la cabecera es válida, validar también el actionForm si hay pasos
      // Si no hay pasos (actionForm vacío), permitir guardar solo la cabecera
      if (this.canSaveHeaderOnly()) {
        // Si el actionForm tiene valores, debe ser válido
        if (this.hasActionData()) {
          return this.actionForm.valid;
        }
        // Si no hay datos de acción, permitir guardar solo la cabecera (solo nuevo paso / drawer vacío)
        if (!this.isEditMode) {
          return true;
        }
        return false;
      }

      return false;
    }

    // Si no hay actionHeader y no es modo nuevo, validar solo actionForm
    return this.actionForm.valid;
  }
}

